"use client";
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

export type IntegrationCategory = {
  id: string;
  name: string;
  icon: React.ReactNode;
};

export type Integration = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  categoryId: string;
  thumbnail?: string;
};

interface FeaturesGridProps {
  categories: IntegrationCategory[];
  integrations: Integration[];
  className?: string;
}

export function FeaturesGrid({ categories, integrations, className }: FeaturesGridProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  const filteredIntegrations = activeCategory 
    ? integrations.filter(integration => integration.categoryId === activeCategory)
    : integrations;

  return (
    <div className={cn("w-full py-12 px-4 md:px-6", className)}>
      <div className="container mx-auto">
        <div className="flex flex-col gap-10">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setActiveCategory(null)}
              className={cn(
                "h-9 px-4 py-2 rounded-md text-sm font-medium",
                "transition-[color,background-color,box-shadow] duration-200 ease-in-out",
                "focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-1",
                "shadow-sm hover:shadow",
                activeCategory === null
                  ? "bg-emerald-500 text-white"
                  : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
              )}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={cn(
                  "h-9 px-4 py-2 rounded-md text-sm font-medium",
                  "transition-[color,background-color,box-shadow] duration-200 ease-in-out",
                  "focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-1",
                  "shadow-sm hover:shadow",
                  "flex items-center gap-2",
                  activeCategory === category.id
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                )}
              >
                <span className="w-4 h-4">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>

          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            layout
          >
            {filteredIntegrations.map((integration) => (
              <motion.div
                key={integration.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className={cn(
                  "group relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden",
                  "border border-gray-200 dark:border-gray-700",
                  "transition-[transform,box-shadow] duration-300 ease-in-out",
                  "hover:shadow-md hover:-translate-y-1",
                  "focus-within:ring-2 focus-within:ring-emerald-500/50",
                  "h-full flex flex-col"
                )}
              >
                {integration.thumbnail && (
                  <div className="aspect-video w-full overflow-hidden">
                    <img 
                      src={integration.thumbnail} 
                      alt={integration.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="mb-4 w-10 h-10 rounded-md bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                    {integration.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{integration.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm flex-grow">{integration.description}</p>
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <span className="inline-flex items-center text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      {categories.find(c => c.id === integration.categoryId)?.name}
                      <svg 
                        className="ml-1.5 w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path 
                          fillRule="evenodd" 
                          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" 
                          clipRule="evenodd" 
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default FeaturesGrid;
